import { DolfinDiagram } from '../diagram'
import DolfinFlowExecutor from '../flow-executor/base'
import { NodeError, NodeWarning } from '../types'
import { DolfinNode } from './base'
import { TypedNode } from './types'

/**
 * Multi-Path node - provides conditional branching with multiple possible paths.
 *
 * @see /docs/domain/nodes/multi-path.md for canonical documentation
 */
export class MultiPathNode extends DolfinNode<DolfinFlowExecutor, 'multi-path'> {
  async process(): Promise<number> {
    await this.loadWeightedTargetAchievement(
      this.formData.targets,
      this.formData.use_target_weights
    )

    if (this.formData.filters && Array.isArray(this.formData.filters)) {
      for (const [index, filter] of this.formData.filters.entries()) {
        if (!!filter) {
          await this.loadUserFields(filter.data ?? [])
          await this.loadMetricAndTargetValues(filter.data ?? [])

          if (!filter.data || this.evaluateLogic(filter.data)) {
            console.info('Path #', index + 1)
            return index
          }
        }
      }
    }

    return -1
  }

  async parse(_diagram: DolfinDiagram) {
    return this.node
  }

  static validate(
    node: TypedNode<'multi-path'>,
    diagram: DolfinDiagram
  ): {
    errors: NodeError[]
    warnings: NodeWarning[]
  } {
    const superValidation = super.validate(node, diagram)

    const hasFilters = node.data.formData?.filters
    superValidation.errors.push(...this.validateWeightedTargetAchievement(node, hasFilters))

    if (!hasFilters) {
      superValidation.errors.push({
        message: 'Multi-path node must have filters',
        node
      })
    }

    if (hasFilters) {
      for (const filter of hasFilters) {
        if (!Object.keys(filter.data ?? {}).length) {
          superValidation.errors.push({
            message: 'Multi-path node must have filters',
            node
          })
        }

        // Make it recursive
        const hasValues = Object.values(filter.data ?? {}).some(
          v => v !== null && v !== undefined && v !== '' && Array.isArray(v) && v.length > 0
        )

        if (!hasValues) {
          superValidation.errors.push({
            message: 'Multi-path node must have values',
            node
          })
        }
      }
    }

    return {
      errors: [...superValidation.errors],
      warnings: [...superValidation.warnings]
    }
  }
}
